import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { apiUrl } from "../config/url";
import Navbar from "./Navbar";
import { useNavigate } from "react-router-dom";

const formatDate = (dateString) => {
  const [year, month, day] = dateString?.split("-");
  return `${day}-${month}-${year}`;
};

const QuotationRegisters = () => {
  const navigate = useNavigate();
  // State variables
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(""); // For the selected financial year
  const [financialYears, setFinancialYears] = useState([]); // For storing fetched financial years
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const user_role = localStorage.getItem("user_role");
  const [coMasters, setCoMasters] = useState([]);
  const [selectedCoId, setSelectedCoId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filterType, setFilterType] = useState("");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [quotations, setQuotations] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [filteredQuotations, setFilteredQuotations] = useState([]);
  const token = localStorage.getItem("api_token");
  const currentYear = new Date().getFullYear().toString();

  useEffect(() => {
    // Fetch financial years from the API
    const fetchFinancialYears = async () => {
      try {
        const company_id = localStorage.getItem("company_id"); // Fetch company_id from localStorage
        const response = await axios.get(`${apiUrl}/admin/financialYearList`, {
          headers: { api_token: token },
          params: { company_id: company_id }, // Use dynamic company_id
        });

        // Extract the financial years from the API response
        const years = response.data.data;
        const sortedYears = years.sort((a, b) => {
          return new Date(b.start_date) - new Date(a.start_date);
        });

        console.log("Sorted Financial Years:", sortedYears);

        setFinancialYears(sortedYears);

        console.log(response.data);
        // Set the default selected financial year based on current year
        const currentYearOption = years.find(
          (year) => year.fin_current_year === currentYear
        );
        if (currentYearOption) {
          setSelectedFinancialYear(currentYearOption.id); // Select by ID
        } else if (sortedYears.length > 0) {
          setSelectedFinancialYear(sortedYears[0].id); // Fallback to the top financial year
        }
      } catch (error) {
        console.error("Error fetching financial years", error);
      }
    };

    fetchFinancialYears();
  }, [token]);

  // Fetch All Quotations initially
  const fetchAllQuotations = async () => {
    setLoading(true);
    try {
      console.log("Response", selectedFinancialYear);
      const response = await axios.get(`${apiUrl}/admin/getAllQuotations`, {
        headers: { api_token: token },
        params: {
          fin_id: selectedFinancialYear, // Send the selected financial year ID directly
        },
      });
      console.log("Quotations fetched:", response.data);
      setQuotations(response.data.data);
    } catch (error) {
      console.error("Error fetching all quotations", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (quot_id) => {
    if (!window.confirm("Are you sure you want to delete this quotation?")) {
      return;
    }

    // Set deleting state for the specific quotation
    setDeleting((prev) => ({ ...prev, [quot_id]: true }));

    try {
      const response = await axios.get(`${apiUrl}/admin/deleteQuotation`, {
        headers: { api_token: token },
        params: { quot_id: quot_id }, // Send 'quot_id' as a query parameter
      });

      if ((response.data.status = true)) {
        alert("Quotation deleted successfully!");
        fetchAllQuotations(); // Refresh the list after deletion
      } else {
        alert("Failed to delete the quotation: " + response.data.message);
      }
    } catch (error) {
      console.error("Error deleting quotation:", error);
      alert("An error occurred while deleting the quotation.");
    } finally {
      // Reset deleting state for the specific quotation
      setDeleting((prev) => ({ ...prev, [quot_id]: false }));
    }
  };

  // Automatically call fetchAllQuotations when the financial year is selected or updated
  useEffect(() => {
    if (selectedFinancialYear) {
      console.log("Selected Financial Year (Initial):", selectedFinancialYear);
      fetchAllQuotations(); // Call the function immediately after selecting the year
    }
  }, [selectedFinancialYear]); // This hook will run when selectedFinancialYear is updated

  // Fetch Co Masters for dropdown
  const fetchCoMasters = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/admin/getAllCoMasters`, {
        headers: { api_token: token },
      });
      setCoMasters(Array.isArray(response.data.data) ? response.data.data : []);
    } catch (error) {
      console.error("Error fetching Co Masters", error);
      toast.error("Failed to fetch Co Masters");
    } finally {
      setLoading(false);
    }
  };

  // Fetch Quotations based on filters
  const fetchFilteredQuotations = async () => {
    setLoading(true);
    try {
      const params = {
        start_date:
          filterType === "date"
            ? startDate
              ? formatDate(startDate)
              : null
            : null,
        end_date:
          filterType === "date" ? (endDate ? formatDate(endDate) : null) : null,
        co_id: filterType === "co_id" ? selectedCoId : null,
        user_id: filterType === "user" ? selectedUserId : null, // Added user_id parameter
        fin_id: selectedFinancialYear || null, // Added financial year parameter
      };

      // Log the parameters being sent to the backend
      console.log("Parameters sent to backend:", params);

      const response = await axios.get(
        `${apiUrl}/admin/filterResultQuotation`,
        {
          headers: { api_token: token },
          params,
        }
      );
      setQuotations(response.data.data.quotationDetails);
    } catch (error) {
      console.error("Error fetching filtered quotations", error);
      toast.error("Failed to fetch filtered quotations");
    } finally {
      setLoading(false);
      setShowModal(false);
    }
  };

  useEffect(() => {
    fetchCoMasters(); // Fetch Co Masters on component mount
  }, []);

  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserList = async () => {
      const token = localStorage.getItem("api_token"); // Get API token from localStorage
      try {
        const response = await axios.get(`${apiUrl}/admin/userList`, {
          headers: { api_token: token }, // Add token to headers
        });
        setUsers(response.data.data); // Set user data to state
      } catch (error) {
        console.error("There was an error fetching the user data!", error);
      } finally {
        setLoading(false); // Set loading to false after fetching
      }
    };

    fetchUserList(); // Call the async function
  }, []);

  // Handle modal submission
  const handleFilter = () => {
    if (
      (filterType === "date" && startDate && endDate) ||
      (filterType === "co_id" && selectedCoId) ||
      (filterType === "user" && selectedUserId)
    ) {
      fetchFilteredQuotations();
    } else {
      toast.error("Please select a filter criteria.");
    }
  };

  const viewQuotations = async (quot_id) => {
    try {
      const response = await axios.get(
        `${apiUrl}/generatePdf?quot_id=${quot_id}`,
        {
          headers: { api_token: token },
          responseType: "blob", // Ensures response is received as a Blob
        }
      );

      if (response.status === 200) {
        const pdfBlob = new Blob([response.data], { type: "application/pdf" });
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const pdfWindow = window.open(pdfUrl, "_blank"); // Opens the PDF in a new tab

        // Automatically release the object URL after opening
        if (pdfWindow) {
          pdfWindow.onload = () => {
            URL.revokeObjectURL(pdfUrl); // Clean up once loaded
          };
        } else {
          console.warn(
            "Failed to open PDF in new tab. Please allow pop-ups for this site."
          );
          URL.revokeObjectURL(pdfUrl); // Clean up immediately if tab couldn't open
        }
      } else {
        console.error(`Failed to fetch PDF: ${response.statusText}`);
        alert("Failed to generate PDF. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching PDF:", error);
      alert(
        "An error occurred while generating the PDF. Please try again later."
      );
    }
  };
  // Filter quotations based on search term
  useEffect(() => {
    if (quotations && Array.isArray(quotations)) {
      // Check if quotations is defined and an array
      const filtered = quotations.filter((quotation) => {
        const quotationNo = quotation.quotation_no?.toString() || "";
        const partyName = quotation.party_name?.toLowerCase() || "";
        const coName = quotation.co_name?.toLowerCase() || "";
        const lowerSearchTerm = searchTerm.toLowerCase();
        return (
          quotationNo.includes(lowerSearchTerm) ||
          partyName.includes(lowerSearchTerm) ||
          coName.includes(lowerSearchTerm)
        );
      });
      setFilteredQuotations(filtered);
    }
  }, [searchTerm, quotations]);

  // Removed redundant fetchFinancialYears useEffect as it's defined earlier

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;

  // Calculate the total pages
  const totalPages = Math.ceil(filteredQuotations.length / itemsPerPage);

  // Calculate the current page's quotations to display
  const currentQuotations = filteredQuotations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Handle page changes
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <>
      <div>
        <Navbar />
        <ToastContainer />
        {/* Display Quotations Table */}
        <div className="container mx-auto mt-6">
          <h1 className="mb-6 text-3xl font-bold text-center">
            Registered Quotations
          </h1>
          {loading ? (
            <div className="py-4 text-center">
              <p>Loading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              {/* Search and Filter Section */}
              <div className="flex items-center justify-between mb-4 space-x-4">
                <div className="flex items-center space-x-4">
                  <input
                    type="text"
                    placeholder="Search Quotations"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-64 px-4 py-2 border border-gray-300 rounded"
                  />
                  <button
                    onClick={() => {
                      setShowModal(true);
                      setSelectedCoId("");
                      setStartDate("");
                      setEndDate("");
                    }}
                    className="px-4 py-2 text-white bg-blue-500 rounded"
                  >
                    Filter
                  </button>
                </div>
                <label className="block text-left text-md">
                  Financial Year
                </label>

                <div className="w-64">
                  <select
                    value={selectedFinancialYear}
                    onChange={(e) => setSelectedFinancialYear(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded"
                  >
                    {financialYears.length > 0 ? (
                      financialYears.map((year) => (
                        <option key={year.id} value={year.id}>
                          {year.fin_year}
                        </option>
                      ))
                    ) : (
                      <option value="">No financial years available</option>
                    )}
                  </select>
                </div>
              </div>

              {/* Modal for Filters */}
              {showModal && (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-75"
                  onClick={() => setShowModal(false)} // Close modal on background click
                >
                  <div
                    className="relative w-1/4 p-6 bg-white rounded-md shadow-md" // Reduced width
                    onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal content
                  >
                    <button
                      onClick={() => setShowModal(false)}
                      className="absolute flex items-center justify-center text-white bg-red-500 rounded-md top-2 right-2 w-7 h-7 hover:bg-red-700"
                      aria-label="Close"
                    >
                      X
                    </button>
                    <h2 className="mb-4 text-xl font-bold">Select Filters</h2>
                    <div className="mb-4">
                      <label className="block mb-2 text-sm">Filter Type</label>
                      <select
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded"
                      >
                        <option value="">Select Filter Type</option>
                        <option value="date">Date</option>
                        <option value="co_id">Co ID</option>
                        {user_role === "1" && (
                          <option value="user">User Wise</option>
                        )}
                      </select>
                    </div>

                    {filterType === "date" && (
                      <>
                        <div className="mb-4">
                          <label className="block mb-2 text-sm">
                            Start Date
                          </label>
                          <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded"
                          />
                        </div>
                        <div className="mb-4">
                          <label className="block mb-2 text-sm">End Date</label>
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded"
                          />
                        </div>
                      </>
                    )}

                    {filterType === "co_id" && (
                      <div className="mb-4">
                        <label className="block mb-2 text-sm">Co ID</label>
                        <select
                          value={selectedCoId}
                          onChange={(e) => setSelectedCoId(e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded"
                        >
                          <option value="">Select Co ID</option>
                          {coMasters.map((coMaster) => (
                            <option key={coMaster.id} value={coMaster.id}>
                              {coMaster.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {user_role === "1" && filterType === "user" && (
                      <div className="mb-4">
                        <label className="block mb-2 text-sm">User</label>
                        <select
                          value={selectedUserId}
                          onChange={(e) => setSelectedUserId(e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded"
                        >
                          <option value="" selected>
                            Select User
                          </option>
                          {users.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.username}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <button
                      onClick={handleFilter}
                      className="w-full px-4 py-2 text-white bg-blue-500 rounded"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              )}

              <h1 className="mb-2 text-lg font-bold text-black-600">
                Total Quotations :{" "}
                {filteredQuotations.length > 0 ? (
                  <>{filteredQuotations.length}</>
                ) : (
                  <>{currentQuotations.length}</>
                )}
              </h1>

              {/* Table Section */}
              <table className="bg-white border border-gray-200 table-auto inline-table">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-center border whitespace-nowrap">
                      Quotation No
                    </th>
                    <th className="px-4 py-2 text-left text-center border whitespace-nowrap">
                      Quotation Date
                    </th>
                    <th className="px-4 py-2 text-left text-center border whitespace-nowrap">
                      Party Name
                    </th>
                    <th className="px-4 py-2 text-left text-center border whitespace-nowrap">
                      C/o Name
                    </th>
                    <th className="px-4 py-2 text-left text-center border whitespace-nowrap">
                      Quotation Amount
                    </th>
                    <th className="px-4 py-2 text-left text-center border whitespace-nowrap">
                      Actions
                    </th>
                    <th className="px-4 py-2 text-left text-center border whitespace-nowrap">
                      Delete
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentQuotations.length > 0 ? (
                    currentQuotations.map((quotation, index) => (
                      <tr key={index} className="text-sm">
                        <td className="px-4 py-2 border">
                          {quotation.quotation_no}
                        </td>
                        <td className="px-4 py-2 border">
                          {quotation.quotation_date
                            ? formatDate(quotation.quotation_date)
                            : ""}
                        </td>
                        <td className="px-4 py-2 border">
                          {quotation.party_name}
                        </td>
                        <td className="px-4 py-2 border">
                          {quotation.co_name}
                        </td>
                        <td className="px-4 py-2 border">
                          {quotation.net_amount}
                        </td>
                        <td className="flex gap-2 px-4 py-2 border">
                          <button
                            onClick={() => viewQuotations(quotation.id)}
                            className="w-full px-4 py-2 text-white bg-blue-500 rounded"
                          >
                            View
                          </button>
                          <button
                            onClick={() => {
                              navigate(`/edit/${quotation.id}`);
                            }}
                            // onClick={() => viewQuotations(quotation.id)}
                            className="w-full px-4 py-2 text-white bg-yellow-500 rounded"
                          >
                            Edit
                          </button>
                        </td>
                        <td className="px-4 py-2 border">
                          <button
                            onClick={() => handleDelete(quotation.id)}
                            className={`w-full px-4 py-2 text-white rounded ${deleting[quotation.id]
                                ? "bg-gray-400"
                                : "bg-red-500"
                              }`}
                            disabled={deleting[quotation.id]} // Disable the button for the specific quotation
                          >
                            {deleting[quotation.id] ? "Deleting..." : "Delete"}
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-4 py-2 text-center border">
                        No quotations found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Pagination Controls */}
              <div className="flex justify-center mt-4">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 mx-1 text-white bg-blue-500 rounded disabled:bg-gray-300"
                >
                  Previous
                </button>
                <span className="px-4 py-2">{`Page ${currentPage} of ${totalPages}`}</span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 mx-1 text-white bg-blue-500 rounded disabled:bg-gray-300"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default QuotationRegisters;
